// Books.js — UPDATED WITH FORMAT PRICING, STORE LINKS, DIRECT PURCHASE URL,
// AVAILABILITY/IS_ACTIVE DEFAULTS, AND AUDIOBOOK TOGGLE

import { useState, useEffect } from "react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import Api from "../../../../shared/api/api";
import BookDescription from "../../components/books/BookDescription";
import {
  bookDescriptionExcerpt,
  bookDescriptionText,
} from "../../helpers/bookDescription";
import "../styles/AdminBooks.css";
import "../styles/AdminSocialPosts.css";
import { DateTime } from "luxon";

function convertToUtc(localDatetime) {
  if (!localDatetime) return null;
  return DateTime.fromISO(localDatetime, { zone: "America/Los_Angeles" })
    .toUTC()
    .toISO({ suppressMilliseconds: true });
}

const descriptionEditorModules = {
  toolbar: [
    ["bold", "italic"],
    [{ list: "ordered" }, { list: "bullet" }],
    ["clean"],
  ],
};

const descriptionEditorFormats = ["bold", "italic", "list"];

export default function Books() {
  const defaultBook = {
    title: "",
    series: "",
    bookNumber: "",
    author: "Kenneth L Powell",
    releaseDate: "",
    publisher: "",
    description: "",
    image: null,

    directPurchase: false,
    direct_purchase_url: "",
    epubFile: null,
    pdfFile: null,
    has_epub: false,
    has_pdf: false,

    // Format Toggles
    ebookToggle: false,
    paperbackToggle: false,
    hardcoverToggle: false,
    audiobookToggle: false,
    sellAudiobookDirectly: false,

    // Ebook fields
    ebook: "",
    ebookPages: "",
    ebookIsbn: "",
    ebook_price: "",

    // Paperback fields
    paperback: "",
    paperbackPages: "",
    paperbackIsbn: "",
    paperback_price: "",

    // Hardcover fields
    hardcover: "",
    hardcoverPages: "",
    hardcoverIsbn: "",
    hardcover_price: "",

    // Audiobook fields
    audiobookLink: "",
    audiobookLength: "",
    audiobook_price: "",

    tags: "",
    featured: false,
  };

  const [books, setBooks] = useState([]);
  const [book, setBook] = useState(defaultBook);
  const [showModal, setShowModal] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [createSocial, setCreateSocial] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");

  // Social post fields
  const [fbBody, setFbBody] = useState("");
  const [igBody, setIgBody] = useState("");
  const [twBody, setTwBody] = useState("");

  // Share modal states
  const [showShareModal, setShowShareModal] = useState(false);
  const [shareBook, setShareBook] = useState(null);
  const [shareFbBody, setShareFbBody] = useState("");
  const [shareFbFirstComment, setShareFbFirstComment] = useState("");
  const [shareIgBody, setShareIgBody] = useState("");
  const [shareIgFirstComment, setShareIgFirstComment] = useState("");
  const [shareTwBody, setShareTwBody] = useState("");
  const [shareFbScheduledAt, setShareFbScheduledAt] = useState("");
  const [shareIgScheduledAt, setShareIgScheduledAt] = useState("");
  const [shareTwScheduledAt, setShareTwScheduledAt] = useState("");

  useEffect(() => {
    fetchBooks();
  }, []);

  const fetchBooks = async () => {
    try {
      const data = await Api.getAllAuthor("books");
      const array = data.books || data;

      const formatted = array.map((b) => ({
        ...b,
        featured: !!b.featured,
        directPurchase: !!b.direct_purchase_url,
        has_epub: !!b.has_epub,
        has_pdf: !!b.has_pdf,
        ebookToggle: !!(b.ebook || b.ebook_price || b.ebook_pages),
        paperbackToggle: !!(b.paperback || b.paperback_price || b.paperback_pages),
        hardcoverToggle: !!(b.hardcover || b.hardcover_price || b.hardcover_pages),
        audiobookToggle: !!(b.audiobook_link || b.audiobook_price || b.audiobook_length),
      }));

      setBooks(formatted);
    } catch (err) {
      console.error("Error loading books:", err);
    }
  };

  const openModal = () => {
    setShowModal(true);
    setIsEditMode(false);
    setBook(defaultBook);
    setCreateSocial(false);
    setSubmitError("");
    resetSocialFields();
  };

  const closeModal = () => {
    setShowModal(false);
    setIsEditMode(false);
    setBook(defaultBook);
    setCreateSocial(false);
    setSubmitError("");
    resetSocialFields();
  };

  const resetSocialFields = () => {
    setFbBody("");
    setIgBody("");
    setTwBody("");
  };

  const handleChange = (e) => {
    const { name, value, type, checked, files } = e.target;
    if (type === "checkbox") {
      setBook((current) => ({
        ...current,
        [name]: checked,
        ...(name === "directPurchase" && checked ? { ebookToggle: true } : {}),
      }));
    } else if (type === "file") {
      setBook({ ...book, [name]: files[0] || null });
    } else {
      setBook({ ...book, [name]: value });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError("");

    if (!bookDescriptionText(book.description)) {
      setSubmitError("Enter a book description.");
      return;
    }

    const needsEpub = book.directPurchase && !book.has_epub && !book.epubFile;
    const needsPdf = book.directPurchase && !book.has_pdf && !book.pdfFile;
    if (needsEpub || needsPdf) {
      setSubmitError("Direct ebook sales require both an EPUB file and a PDF file.");
      return;
    }

    setIsSubmitting(true);
    try {
      let imageUrl = book.image;
      if (book.image instanceof File) {
        const form = new FormData();
        form.append("imageFile", book.image);
        form.append("related_type", "book");
        form.append("related_id", book.title);
        form.append("alt_text", book.title);
        form.append("is_primary", "true");

        const res = await Api.uploadImage(form);
        imageUrl = res.url;
      }

      // Null-safe pricing
      const safe = (val) => (val === "" ? null : val);

      const directPurchaseUrl = book.directPurchase
        ? `https://www.kennethlpowell.com/books/${encodeURIComponent(book.title)}`
        : null;
      const payload = {
        title: book.title,
        series: book.series,
        bookNumber: book.bookNumber,
        author: "Kenneth L Powell",
        releaseDate: book.releaseDate,
        publisher: book.publisher,
        description: book.description,
        image: imageUrl,

        direct_purchase_url: directPurchaseUrl,

        // Non-direct store links
        ebook: book.ebook || null,
        paperback: book.paperback || null,
        hardcover: book.hardcover || null,
        audiobook_link: book.audiobookToggle ? book.audiobookLink || null : null,

        // Format metadata
        ebook_pages: book.ebookPages || null,
        ebook_isbn: book.ebookIsbn || null,
        paperback_pages: book.paperbackPages || null,
        paperback_isbn: book.paperbackIsbn || null,
        hardcover_pages: book.hardcoverPages || null,
        hardcover_isbn: book.hardcoverIsbn || null,
        audiobook_length: book.audiobookToggle ? book.audiobookLength || null : null,

        // Prices
        ebook_price: safe(book.ebook_price),
        paperback_price: safe(book.paperback_price),
        hardcover_price: safe(book.hardcover_price),
        audiobook_price: book.audiobookToggle && book.sellAudiobookDirectly
          ? safe(book.audiobook_price) : null,

        // Availability defaults
        availability: book.availability ?? 0,
        is_active: book.is_active ?? 0,

        featured: book.featured ? 1 : 0,
        tags: book.tags || "",
      };

      const hasNewEbookFiles = Boolean(book.epubFile || book.pdfFile);
      const uploadEbookFiles = async () => {
        if (!book.directPurchase || !hasNewEbookFiles) return;

        const files = new FormData();
        if (book.epubFile) files.append("epub", book.epubFile);
        if (book.pdfFile) files.append("pdf", book.pdfFile);
        files.append("series", book.series || "");
        files.append("require_both", String(!book.has_epub || !book.has_pdf));
        files.append("enable_direct", "true");
        files.append("direct_purchase_url", directPurchaseUrl);
        await Api.uploadBookFiles(book.title, files);
      };

      if (isEditMode) {
        await uploadEbookFiles();
        await Api.updateBook(book.title, payload);
      } else {
        await Api.createBook({
          ...payload,
          direct_purchase_url: book.directPurchase ? null : payload.direct_purchase_url,
        });
        await uploadEbookFiles();

        if (createSocial) {
          if (fbBody.trim()) {
            await Api.createSocialPost({
              platform: "facebook",
              content: fbBody,
              image: null,
            });
          }
          if (igBody.trim()) {
            await Api.createSocialPost({
              platform: "instagram",
              content: igBody,
              image: null,
            });
          }
          if (twBody.trim()) {
            await Api.createSocialPost({
              platform: "twitter",
              content: twBody,
            });
          }
        }
      }

      await fetchBooks();
      closeModal();
    } catch (err) {
      console.error("Error submitting book:", err);
      setSubmitError(err.message || "The book could not be saved.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEdit = (b) => {
    setBook({
      ...defaultBook,
      ...b,
      bookNumber: b.book_number ?? "",
      releaseDate: b.release_date ? String(b.release_date).slice(0, 10) : "",
      ebookPages: b.ebook_pages ?? "",
      ebookIsbn: b.ebook_isbn ?? "",
      paperbackPages: b.paperback_pages ?? "",
      paperbackIsbn: b.paperback_isbn ?? "",
      hardcoverPages: b.hardcover_pages ?? "",
      hardcoverIsbn: b.hardcover_isbn ?? "",
      audiobookLink: b.audiobook_link ?? "",
      audiobookLength: b.audiobook_length ?? "",
      directPurchase: !!b.direct_purchase_url,
      epubFile: null,
      pdfFile: null,
      has_epub: !!b.has_epub,
      has_pdf: !!b.has_pdf,
      ebookToggle: !!(b.ebook || b.ebook_price),
      paperbackToggle: !!(b.paperback || b.paperback_price),
      hardcoverToggle: !!(b.hardcover || b.hardcover_price),
      audiobookToggle: !!(b.audiobook_link || b.audiobook_price),
      sellAudiobookDirectly: Number(b.audiobook_price) > 0,
    });

    setIsEditMode(true);
    setShowModal(true);
    setSubmitError("");
    resetSocialFields();
  };

  const handleDelete = async (title) => {
    try {
      await Api.deleteBook(title);
      await fetchBooks();
    } catch (err) {
      console.error("Delete failed:", err);
    }
  };

  const toggleFeatured = async (title) => {
    try {
      const b = books.find((x) => x.title === title);
      await Api.updateBook(title, { featured: b.featured ? 0 : 1 });
      await fetchBooks();
    } catch (err) {
      console.error(err);
    }
  };

  const handleShare = (b) => {
    const url = `https://kennethlpowell.com/books/${encodeURIComponent(b.title)}`;
    setShareBook(b);
    setShareFbBody("");
    setShareFbFirstComment(url);
    setShareIgBody("");
    setShareIgFirstComment(url);
    setShareTwBody(url);
    setShowShareModal(true);
  };

  const closeShareModal = () => {
    setShowShareModal(false);
    setShareBook(null);
  };

  const handleSubmitShare = async () => {
    const img = shareBook.image || null;

    try {
      if (shareFbBody.trim()) {
        await Api.createSocialPost({
          platform: "facebook",
          content: shareFbBody,
          image: img,
          first_comment: shareFbFirstComment,
          scheduled_at: convertToUtc(shareFbScheduledAt),
        });
      }

      if (shareIgBody.trim()) {
        await Api.createSocialPost({
          platform: "instagram",
          content: shareIgBody,
          image: img,
          first_comment: shareIgFirstComment,
          scheduled_at: convertToUtc(shareIgScheduledAt),
        });
      }

      if (shareTwBody.trim()) {
        await Api.createSocialPost({
          platform: "twitter",
          content: shareTwBody,
          scheduled_at: convertToUtc(shareTwScheduledAt),
        });
      }

      alert("Social posts created!");
      closeShareModal();
    } catch (err) {
      console.error("Share error:", err);
      alert("Failed to share.");
    }
  };

  return (
    <div className="adminPageContainer">
      <button className="addBookBtn" onClick={openModal}>
        Add Book
      </button>

      {showModal && (
        <div className="modalOverlay">
          <div className="modalContent">
            <h2>{isEditMode ? "Edit Book" : "Add Book"}</h2>

            <form className="bookFormGrouped" onSubmit={handleSubmit}>
              <div className="formSection">
                <label>Title</label>
                <input
                  type="text"
                  name="title"
                  value={book.title}
                  onChange={handleChange}
                  required
                  disabled={isEditMode}
                />

                <label>Series</label>
                <input type="text" name="series" value={book.series} onChange={handleChange} />

                <label>Book Number</label>
                <input type="number" name="bookNumber" value={book.bookNumber} onChange={handleChange} />

                <label>Author</label>
                <input type="text" name="author" value={book.author} onChange={handleChange} />

                <label>Release Date</label>
                <input type="date" name="releaseDate" value={book.releaseDate} onChange={handleChange} />

                <label>Publisher</label>
                <input type="text" name="publisher" value={book.publisher} onChange={handleChange} />

                <label>Description</label>
                <div className="bookDescriptionEditor">
                  <ReactQuill
                    theme="snow"
                    value={book.description}
                    onChange={(description) =>
                      setBook((current) => ({ ...current, description }))
                    }
                    modules={descriptionEditorModules}
                    formats={descriptionEditorFormats}
                    placeholder="Write the book description..."
                  />
                </div>

                {bookDescriptionText(book.description) && (
                  <div className="bookDescriptionPreview">
                    <h3>Preview</h3>
                    <BookDescription description={book.description} />
                  </div>
                )}
              </div>

              <div className="formSection">
                <label>Cover Image</label>
                <input type="file" name="image" accept="image/*" onChange={handleChange} />

                {typeof book.image === "string" && book.image && (
                  <img
                    src={book.image.includes("?") ? `${book.image}&raw=1` : `${book.image}?raw=1`}
                    alt={book.title}
                    className="adminBookThumb"
                  />
                )}

                <label className="bookCheckboxRow">
                  <input
                    type="checkbox"
                    name="directPurchase"
                    checked={book.directPurchase}
                    onChange={handleChange}
                  />
                  Sell this book directly
                </label>

                {book.directPurchase && (
                  <div className="ebookUploadFields">
                    <label>EPUB File</label>
                    <input
                      type="file"
                      name="epubFile"
                      accept=".epub,application/epub+zip"
                      onChange={handleChange}
                      required={!book.has_epub}
                    />
                    {book.has_epub && !book.epubFile && (
                      <p className="ebookFileStatus">EPUB is already loaded. Select a file to replace it.</p>
                    )}

                    <label>PDF File</label>
                    <input
                      type="file"
                      name="pdfFile"
                      accept=".pdf,application/pdf"
                      onChange={handleChange}
                      required={!book.has_pdf}
                    />
                    {book.has_pdf && !book.pdfFile && (
                      <p className="ebookFileStatus">PDF is already loaded. Select a file to replace it.</p>
                    )}
                  </div>
                )}

                {/* ---- FORMAT TOGGLES ---- */}
                <h3>Formats</h3>

                <label>
                  <input
                    type="checkbox"
                    name="ebookToggle"
                    checked={book.ebookToggle}
                    onChange={handleChange}
                  />
                  Ebook Available
                </label>

                {book.ebookToggle && (
                  <>
                    {!book.directPurchase && (
                      <>
                        <label>Amazon or External Ebook Link</label>
                        <input type="url" name="ebook" value={book.ebook || ""} onChange={handleChange} />
                      </>
                    )}

                    <label>Ebook Pages</label>
                    <input type="number" name="ebookPages" value={book.ebookPages || ""} onChange={handleChange} />

                    <label>Ebook ISBN</label>
                    <input type="text" name="ebookIsbn" value={book.ebookIsbn || ""} onChange={handleChange} />

                    <label>Ebook Price</label>
                    <input
                      type="number"
                      step="0.01"
                      name="ebook_price"
                      value={book.ebook_price || ""}
                      onChange={handleChange}
                    />
                  </>
                )}

                <label>
                  <input
                    type="checkbox"
                    name="paperbackToggle"
                    checked={book.paperbackToggle}
                    onChange={handleChange}
                  />
                  Paperback Available
                </label>

                {book.paperbackToggle && (
                  <>
                    {!book.directPurchase && (
                      <>
                        <label>Amazon or External Paperback Link</label>
                        <input type="url" name="paperback" value={book.paperback || ""} onChange={handleChange} />
                      </>
                    )}

                    <label>Paperback Pages</label>
                    <input type="number" name="paperbackPages" value={book.paperbackPages || ""} onChange={handleChange} />

                    <label>Paperback ISBN</label>
                    <input type="text" name="paperbackIsbn" value={book.paperbackIsbn || ""} onChange={handleChange} />

                    <label>Paperback Price</label>
                    <input
                      type="number"
                      step="0.01"
                      name="paperback_price"
                      value={book.paperback_price || ""}
                      onChange={handleChange}
                    />
                  </>
                )}

                <label>
                  <input
                    type="checkbox"
                    name="hardcoverToggle"
                    checked={book.hardcoverToggle}
                    onChange={handleChange}
                  />
                  Hardcover Available
                </label>

                {book.hardcoverToggle && (
                  <>
                    {!book.directPurchase && (
                      <>
                        <label>Amazon or External Hardcover Link</label>
                        <input type="url" name="hardcover" value={book.hardcover || ""} onChange={handleChange} />
                      </>
                    )}

                    <label>Hardcover Pages</label>
                    <input type="number" name="hardcoverPages" value={book.hardcoverPages || ""} onChange={handleChange} />

                    <label>Hardcover ISBN</label>
                    <input type="text" name="hardcoverIsbn" value={book.hardcoverIsbn || ""} onChange={handleChange} />

                    <label>Hardcover Price</label>
                    <input
                      type="number"
                      step="0.01"
                      name="hardcover_price"
                      value={book.hardcover_price || ""}
                      onChange={handleChange}
                    />
                  </>
                )}

                <label>
                  <input
                    type="checkbox"
                    name="audiobookToggle"
                    checked={book.audiobookToggle}
                    onChange={handleChange}
                  />
                  Audiobook Available
                </label>

                {book.audiobookToggle && (
                  <>
                    <label>Audiobook Non-Direct Store Link</label>
                    <input
                      type="text"
                      name="audiobookLink"
                      value={book.audiobookLink || ""}
                      onChange={handleChange}
                    />

                    <label>Audiobook Length</label>
                    <input
                      type="text"
                      name="audiobookLength"
                      value={book.audiobookLength || ""}
                      onChange={handleChange}
                    />

                    <label className="bookCheckboxRow">
                      <input
                        type="checkbox"
                        name="sellAudiobookDirectly"
                        checked={book.sellAudiobookDirectly}
                        onChange={handleChange}
                      />
                      Sell audiobook directly
                    </label>
                    {book.sellAudiobookDirectly && <>
                    <label htmlFor="audiobook-price">Direct Audiobook Price</label>
                    <input
                      id="audiobook-price"
                      type="number"
                      min="0.01"
                      step="0.01"
                      required
                      name="audiobook_price"
                      value={book.audiobook_price || ""}
                      onChange={handleChange}
                    />
                    </>}
                  </>
                )}

                <label>Tags (comma-separated)</label>
                <input type="text" name="tags" value={book.tags} onChange={handleChange} />
              </div>

              {/* ---- SOCIAL POSTS ---- */}
              <div className="formSection">
                <h3>Social Posts (optional)</h3>

                <label>
                  <input
                    type="checkbox"
                    checked={createSocial}
                    onChange={(e) => setCreateSocial(e.target.checked)}
                  />
                  Create social posts when adding this book
                </label>

                {createSocial && (
                  <>
                    <label>Facebook Post</label>
                    <textarea value={fbBody} onChange={(e) => setFbBody(e.target.value)} rows={3} />

                    <label>Instagram Post</label>
                    <textarea value={igBody} onChange={(e) => setIgBody(e.target.value)} rows={3} />

                    <label>Twitter/X Post</label>
                    <textarea
                      value={twBody}
                      onChange={(e) => setTwBody(e.target.value)}
                      rows={3}
                      maxLength={280}
                    />
                    <p>{twBody.length} / 280</p>
                  </>
                )}
              </div>

              <div className="buttonRow">
                {submitError && <p className="bookSubmitError">{submitError}</p>}
                <button className="publishingSubmitBtn" type="submit" disabled={isSubmitting}>
                  {isSubmitting ? "Saving..." : isEditMode ? "Update Book" : "Submit Book"}
                </button>
                <button className="cancelBtn" type="button" onClick={closeModal}>
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ---- BOOK LIST ---- */}
      <div className="adminBooksList">
        <h1>Manage Books</h1>

        {books.map((b) => (
          <div className="adminBookRow" key={b.title}>
            <img
              src={
                b.image
                  ? b.image.includes("?")
                    ? `${b.image}&raw=1`
                    : `${b.image}?raw=1`
                  : "https://via.placeholder.com/60x90?text=No+Cover"
              }
              alt={b.title}
              className="adminBookThumb"
            />

            <div className="adminBookInfo">
              <h3>{b.title}</h3>
              <p>{b.series} — Book {b.book_number}</p>
              <p>Release: {b.release_date}</p>
              <p className="blogContentSnippet">
                {bookDescriptionExcerpt(b.description, 120)}
              </p>
            </div>

            <div className="adminBookActions">
              <button className="adminBookEditBtn" onClick={() => handleEdit(b)}>
                Edit
              </button>
              <button className="adminBookDeleteBtn" onClick={() => handleDelete(b.title)}>
                Delete
              </button>
              <button className="adminMerchShareBtn" onClick={() => handleShare(b)}>
                Share
              </button>

              <img
                src={
                  b.featured
                    ? "https://icons.getbootstrap.com/assets/icons/star-fill.svg"
                    : "https://icons.getbootstrap.com/assets/icons/star.svg"
                }
                alt="Toggle Featured"
                className={`featureIcon ${b.featured ? "filled" : ""}`}
                onClick={() => toggleFeatured(b.title)}
              />
            </div>
          </div>
        ))}
      </div>

      {/* ---- SHARE MODAL ---- */}
      {showShareModal && (
        <div className="modalOverlay" onClick={closeShareModal}>
          <div className="modalContent" onClick={(e) => e.stopPropagation()}>
            <h2>Share Book</h2>

            <div className="platformsContainer">
              <div className="formSection">
                <h3>Facebook</h3>
                <textarea value={shareFbBody} onChange={(e) => setShareFbBody(e.target.value)} />
                <input
                  type="datetime-local"
                  value={shareFbScheduledAt}
                  onChange={(e) => setShareFbScheduledAt(e.target.value)}
                />
                <input
                  type="text"
                  value={shareFbFirstComment}
                  onChange={(e) => setShareFbFirstComment(e.target.value)}
                />
              </div>

              <div className="formSection">
                <h3>Instagram</h3>
                <textarea value={shareIgBody} onChange={(e) => setShareIgBody(e.target.value)} />
                <input
                  type="datetime-local"
                  value={shareIgScheduledAt}
                  onChange={(e) => setShareIgScheduledAt(e.target.value)}
                />
                <input
                  type="text"
                  value={shareIgFirstComment}
                  onChange={(e) => setShareIgFirstComment(e.target.value)}
                />
              </div>

              <div className="formSection">
                <h3>Twitter/X</h3>
                <textarea value={shareTwBody} onChange={(e) => setShareTwBody(e.target.value)} />
                <p>{shareTwBody.length} / 280</p>
                <input
                  type="datetime-local"
                  value={shareTwScheduledAt}
                  onChange={(e) => setShareTwScheduledAt(e.target.value)}
                />
              </div>
            </div>

            <div className="buttonRow">
              <button className="publishingSubmitBtn" onClick={handleSubmitShare}>
                Post
              </button>
              <button className="cancelBtn" onClick={closeShareModal}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
